import { PaymentMethod, type CompanyGig } from "model/Gig";
import { useRef, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import type { TFunction } from "i18next";
import { gig as gigService } from "services/api/company/gig.service";
import type {
  EditPaymentType,
  EditSkillItem,
} from "./teamPublicDetailsModal.contract";
import { buildUpdateGigPayload } from "./teamPublicDetailsModal.mapper";

const MIN_WORKERS = 1;
const MIN_RATE = 0;

const getEditPaymentType = (value: string): EditPaymentType => {
  switch (value) {
    case "":
    case PaymentMethod.NONE:
    case PaymentMethod.SIMFLEXITY_PAY:
    case PaymentMethod.GIGAPAY:
      return value;
    default:
      return "";
  }
};

interface UseTeamPublicDetailsEditStateParams {
  companyId?: number;
  gigData: CompanyGig | null;
  setGigData: Dispatch<SetStateAction<CompanyGig | null>>;
  t: TFunction;
}

interface UseTeamPublicDetailsEditStateResult {
  isEditing: boolean;
  isSaving: boolean;
  saveError: string | null;
  editRole: string;
  editAmountOfWorkers: string;
  editPaymentType: EditPaymentType;
  editMinRate: string;
  editMaxRate: string;
  editSkillInput: string;
  editSkills: ReadonlyArray<EditSkillItem>;
  editDescription: string;
  setEditRole: (value: string) => void;
  setEditAmountOfWorkers: (value: string) => void;
  setEditPaymentType: (value: string) => void;
  setEditMinRate: (value: string) => void;
  setEditMaxRate: (value: string) => void;
  setEditSkillInput: (value: string) => void;
  setEditDescription: (value: string) => void;
  handleStartEdit: () => void;
  handleCancelEdit: () => void;
  handleAddSkill: () => void;
  handleRemoveSkill: (skillToRemove: EditSkillItem) => void;
  handleSaveEdit: () => Promise<void>;
  setSaveErrorText: (value: string | null) => void;
}

export const useTeamPublicDetailsEditState = (
  params: UseTeamPublicDetailsEditStateParams,
): UseTeamPublicDetailsEditStateResult => {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [editRole, setEditRole] = useState("");
  const [editAmountOfWorkers, setEditAmountOfWorkers] = useState("");
  const [editPaymentType, setEditPaymentType] = useState<EditPaymentType>("");
  const [editMinRate, setEditMinRate] = useState("");
  const [editMaxRate, setEditMaxRate] = useState("");
  const [editSkillInput, setEditSkillInput] = useState("");
  const [editSkills, setEditSkills] = useState<EditSkillItem[]>([]);
  const [editDescription, setEditDescription] = useState("");
  const draftSkillCounterRef = useRef(0);

  const createDraftSkill = (skill: string): EditSkillItem => {
    const nextCounter = draftSkillCounterRef.current + 1;
    draftSkillCounterRef.current = nextCounter;
    return {
      type: "draft",
      localId: `draft-skill-${nextCounter}`,
      skill,
    };
  };

  const handleStartEdit = (): void => {
    if (!params.gigData) return;
    setEditRole(params.gigData.role ?? "");
    setEditAmountOfWorkers(String(params.gigData.amountOfWorkers ?? 0));
    setEditPaymentType(getEditPaymentType(params.gigData.paymentType ?? ""));
    setEditMinRate(String(params.gigData.minRate ?? 0));
    setEditMaxRate(String(params.gigData.maxRate ?? 0));
    setEditSkills(
      params.gigData.skills
        ?.map((skillItem) => {
          const trimmedSkill = skillItem.skill?.trim() ?? "";
          if (skillItem.id != null) {
            return {
              type: "persisted",
              id: skillItem.id,
              skill: trimmedSkill,
            } as const;
          }
          return createDraftSkill(trimmedSkill);
        })
        .filter((skillItem) => skillItem.skill.length > 0) ?? [],
    );
    setEditSkillInput("");
    setEditDescription(params.gigData.description ?? "");
    setSaveError(null);
    setIsEditing(true);
  };

  const handleCancelEdit = (): void => {
    setIsEditing(false);
    setSaveError(null);
  };

  const handleAddSkill = (): void => {
    const normalizedSkill = editSkillInput.trim();
    if (!normalizedSkill) return;
    const exists = editSkills.some(
      (skillItem) =>
        skillItem.skill.toLocaleLowerCase() ===
        normalizedSkill.toLocaleLowerCase(),
    );
    if (exists) {
      setEditSkillInput("");
      return;
    }
    setEditSkills((prev) => [...prev, createDraftSkill(normalizedSkill)]);
    setEditSkillInput("");
  };

  const handleRemoveSkill = (skillToRemove: EditSkillItem): void => {
    setEditSkills((prev) =>
      prev.filter((skillItem) => {
        if (
          skillItem.type === "persisted" &&
          skillToRemove.type === "persisted"
        ) {
          return skillItem.id !== skillToRemove.id;
        }

        if (skillItem.type === "draft" && skillToRemove.type === "draft") {
          return skillItem.localId !== skillToRemove.localId;
        }

        return true;
      }),
    );
  };

  const handleSaveEdit = async (): Promise<void> => {
    if (!params.companyId || !params.gigData || isSaving) return;
    const parsedAmountOfWorkers = Number(editAmountOfWorkers);
    const parsedMinRate = Number(editMinRate);
    const parsedMaxRate = Number(editMaxRate);
    const trimmedRole = editRole.trim();
    if (trimmedRole.length === 0) {
      setSaveError(params.t("General.Error"));
      return;
    }
    if (
      !Number.isFinite(parsedAmountOfWorkers) ||
      !Number.isFinite(parsedMinRate) ||
      !Number.isFinite(parsedMaxRate)
    ) {
      setSaveError(params.t("General.Error"));
      return;
    }
    if (
      parsedAmountOfWorkers < MIN_WORKERS ||
      parsedMinRate < MIN_RATE ||
      parsedMaxRate < MIN_RATE ||
      parsedMinRate > parsedMaxRate
    ) {
      setSaveError(params.t("General.Error"));
      return;
    }

    setIsSaving(true);
    setSaveError(null);
    try {
      const payload = buildUpdateGigPayload({
        gigData: params.gigData,
        editPaymentType,
        editRole: trimmedRole,
        editDescription,
        parsedMinRate,
        parsedMaxRate,
        parsedAmountOfWorkers,
        editSkills,
      });
      const response = await gigService.updateGig(
        params.companyId,
        payload,
        params.gigData.id,
      );
      params.setGigData(response.data);
      setIsEditing(false);
    } catch {
      setSaveError(params.t("General.Error"));
    } finally {
      setIsSaving(false);
    }
  };

  const setSaveErrorText = (value: string | null): void => {
    setSaveError(value);
  };

  return {
    isEditing,
    isSaving,
    saveError,
    editRole,
    editAmountOfWorkers,
    editPaymentType,
    editMinRate,
    editMaxRate,
    editSkillInput,
    editSkills,
    editDescription,
    setEditRole,
    setEditAmountOfWorkers,
    setEditPaymentType: (value: string): void => {
      setEditPaymentType(getEditPaymentType(value));
    },
    setEditMinRate,
    setEditMaxRate,
    setEditSkillInput,
    setEditDescription,
    handleStartEdit,
    handleCancelEdit,
    handleAddSkill,
    handleRemoveSkill,
    handleSaveEdit,
    setSaveErrorText,
  };
};
